import React, { useState } from "react";
import {
  TextField,
  Stack,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Paper,
} from "@mui/material";

const validateVAT = (vat) => {
  // Must be exactly 8 digits
  if (!/^\d{8}$/.test(vat)) return false;

  // Logic multipliers
  const constants = [1, 2, 1, 2, 1, 2, 4, 1];
  const chars = vat.split("").map(Number);

  // Multiply each digit by corresponding constant
  const result = chars.map((num, index) => num * constants[index]);

  // Sum of all individual digits
  const sum = result
    .map((num) =>
      String(num)
        .split("")
        .reduce((a, b) => a + Number(b), 0)
    )
    .reduce((a, b) => a + b, 0);

  // Special case: 7th digit is 7
  if (chars[6] === 7) {
    return (sum - 10) % 5 === 0 || (sum - 9) % 5 === 0;
  }

  // Otherwise, sum must be divisible by 5
  return sum % 5 === 0;
};

const InvoiceForm = ({ onSubmit }) => {
  const [selectedOption, setSelectedOption] = useState(""); // Track selected option
  const [invoiceCarrier, setInvoiceCarrier] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [error, setError] = useState("");

  const handleSelectionChange = (e) => {
    setSelectedOption(e.target.value);
    setInvoiceCarrier("");
    setBusinessNumber("");
    setError(""); // Clear error when changing selection
  };

  const handleInputChange = (type, value) => {
    if (type === "carrier") {
      setInvoiceCarrier(value);
      setBusinessNumber(""); // Ensure only one field is filled
    } else {
      // Ensure only numeric input for 統一編號
      if (!/^\d*$/.test(value)) return;
      setBusinessNumber(value);
      setInvoiceCarrier(""); // Ensure only one field is filled
    }

    if (!value) {
      setError("");
      return;
    }

    // Validate VAT if user selects 統一編號
    if (type === "business" && value.length === 8) {
      if (!validateVAT(value)) {
        setError("無效的統一編號，請檢查您的輸入");
        return;
      }
    }

    setError(""); // Clear error on valid input

    // Call onSubmit immediately when input changes
    onSubmit({
      invoiceCarrier: type === "carrier" ? value : "",
      businessNumber: type === "business" ? value : "",
    });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        marginBottom:1,
        borderRadius: 2,
      }}
    >
      <Stack >
        <Typography variant="h6" align="center">
          發票資訊
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={selectedOption}
            onChange={handleSelectionChange}
            sx={{ justifyContent: "center" }}
          >
            <FormControlLabel
              value="carrier"
              control={<Radio />}
              label="發票載具"
            />
            <FormControlLabel
              value="business"
              control={<Radio />}
              label="統一編號"
            />
          </RadioGroup>
          {selectedOption === "carrier" && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                value={invoiceCarrier}
                size="small"
                onChange={(e) => handleInputChange("carrier", e.target.value)}
                placeholder="請輸入發票載具"
                autoFocus
              />
            </Box>
          )}
          {selectedOption === "business" && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={businessNumber}
                onChange={(e) => handleInputChange("business", e.target.value)}
                placeholder="請輸入統一編號"
                autoFocus
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 8,
                }}
              />
            </Box>
          )}
        </FormControl>
        {error && (
          <Typography color="error" align="center">
            {error}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default InvoiceForm;
